/**
 * Contact d'un compte créé par l'équipe : e-mail OU numéro WhatsApp (au moins un).
 *
 * Au Mali le numéro WhatsApp sert bien plus que l'e-mail : tous les formulaires de création de compte
 * proposent les deux, l'e-mail reste encouragé (« E-mail (recommandé) ») mais n'est jamais obligatoire.
 * Module PUR (aucun réseau) : la règle « au moins un des deux » et le corps envoyé aux edge functions
 * (`create-merchant-account`, `create-driver-account`, `create-merchant-staff`, `invite-member`) se testent sans navigateur.
 *
 * Les règles de numéro (table des pays, E.164) viennent du module partagé `otp-whatsapp` — jamais recopiées ici.
 */
import { normaliserTelephone } from '../../../../../web-commun/connexion-whatsapp/api'
import type { ValeurTelephone } from '../../../../../web-commun/connexion-whatsapp/ChampTelephone'
import { afficherTelephone } from '../../../../supabase/functions/_shared/otp-whatsapp'

/** Même contrôle que les edge functions : la console refuse avant l'appel ce que le serveur refuserait. */
export const FORMAT_EMAIL_CONTACT = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const MESSAGE_CONTACT_REQUIS = 'Renseignez au moins un e-mail ou un numéro WhatsApp.'
export const MESSAGE_EMAIL_INVALIDE = 'Adresse e-mail invalide.'

/** Ce que saisit la personne : un e-mail (peut rester vide) et un numéro (peut rester vide). */
export interface ContactSaisi {
  email: string
  telephone: ValeurTelephone
}

/** Contact validé et normalisé : e-mail en minuscules, numéro en E.164, `pays` = code ISO du numéro. */
export interface ContactValide {
  email: string | null
  phone: string | null
  pays: string | null
}

/** Champ à mettre en évidence : 'contact' = ni l'un ni l'autre n'est renseigné. */
export type ChampContact = 'email' | 'telephone' | 'contact'

export type ResultatContact =
  | { ok: true; contact: ContactValide }
  | { ok: false; champ: ChampContact; message: string }

/**
 * Règle « au moins un des deux » + validité de chacun de ceux qui sont renseignés.
 * Un champ renseigné mais faux est refusé même si l'autre est bon (on ne crée pas un compte avec un numéro
 * qui porterait une faute de frappe : c'est lui qui recevrait les codes).
 */
export function validerContact({ email, telephone }: ContactSaisi): ResultatContact {
  const mail = email.trim().toLowerCase()
  const aTexte = telephone.texte.trim() !== ''
  if (!mail && !aTexte) return { ok: false, champ: 'contact', message: MESSAGE_CONTACT_REQUIS }
  if (mail && !FORMAT_EMAIL_CONTACT.test(mail)) return { ok: false, champ: 'email', message: MESSAGE_EMAIL_INVALIDE }
  if (!aTexte) return { ok: true, contact: { email: mail, phone: null, pays: null } }
  const r = normaliserTelephone(telephone.texte, telephone.iso)
  if (!r.ok) return { ok: false, champ: 'telephone', message: r.message }
  return { ok: true, contact: { email: mail || null, phone: r.e164, pays: r.iso } }
}

/** Partie « contact » du corps envoyé à l'edge function : n'inclut que ce qui est renseigné (jamais `null`, jamais `''`). */
export function corpsContact(c: ContactValide): { email?: string; phone?: string; pays?: string } {
  return {
    ...(c.email ? { email: c.email } : {}),
    ...(c.phone ? { phone: c.phone, ...(c.pays ? { pays: c.pays } : {}) } : {}),
  }
}

/** Contact à afficher dans un tableau : l'e-mail s'il existe, sinon le numéro mis en forme, sinon rien. */
export function contactAffiche(email: string | null | undefined, telephone: string | null | undefined): string {
  return (email ?? '').trim() || afficherTelephone(telephone)
}

/** Nom lisible d'un compte : le nom, sinon la partie locale de l'e-mail, sinon le numéro, sinon `repli`. */
export function nomLisible(
  nom: string | null | undefined,
  email: string | null | undefined,
  telephone: string | null | undefined,
  repli = '',
): string {
  return (nom ?? '').trim() || (email ?? '').split('@')[0]?.trim() || afficherTelephone(telephone) || repli
}
